import {
  edgeYears,
  GRAPH_MAX_NODES,
  GRAPH_MAX_SEEDS,
  type GraphEdge,
  type GraphFilter,
  type GraphNode,
  type GraphViewState,
  type SavedView,
  type SchemaSnapshot,
  whyText,
} from '@hoopoe/shared';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { MultiDirectedGraph } from 'graphology';
import { Download, Filter, Loader2, Lock, Network, PinOff, Route as RouteIcon, Save, Shuffle, X } from 'lucide-react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useReauth } from '../components/auth/reauth';
import { ColorLegend } from '../components/colors/color-by';
import { DataNumber } from '../components/data/data-text';
import { REVIEW_STATUS_LABELS } from '../components/entities/entity-link';
import { exportPng, exportSvg } from '../components/graph/export';
import {
  type EdgeAttrs,
  fitToGraph,
  type Graph,
  GraphCanvas,
  type GraphRenderer,
  layoutArea,
  layoutNodeOf,
  type NodeAttrs,
  pushFrom,
} from '../components/graph/graph-canvas';
import { shortLabel } from '../components/graph/labels';
import { computeLayout, LAYOUT_MODES, type LayoutMode } from '../components/graph/layout';
import { nodeImage } from '../components/graph/node-image';
import { EntityList, FilterPanel, HiddenList, NodeName, Section, SelectionPanel, ViewsMenu } from '../components/graph/panels';
import { EmptyState } from '../components/guidance/empty-state';
import { EntityAutocomplete, type EntitySuggestion } from '../components/guidance/entity-autocomplete';
import { FieldHelp } from '../components/guidance/field-help';
import { Tip } from '../components/guidance/tip';
import { useScreenTour } from '../components/guidance/tour';
import { useChecklist } from '../components/help/checklist';
import { PageHeader } from '../components/layout/app-shell';
import { Button } from '../components/ui/button';
import { useOptionalConfirm } from '../components/ui/confirm';
import { ApiError, api } from '../lib/api';
import { useStyleOf } from '../lib/colors';
import { shapeOf, useEntitySearch } from '../lib/entities';
import {
  edgeWidth,
  emptyLoaded,
  expand,
  type Loaded,
  linksAmong,
  merge,
  shortestPath,
  TRANSPARENT,
  useSavedViews,
  useThemeColors,
  useViewMutations,
  visibleSubgraph,
  withAlpha,
} from '../lib/graph';
import { useSchema } from '../lib/schema';
import { useCan } from '../lib/session';

// Network graph explorer (PLAN §9, §12.7 screen 7; task 24): Sigma.js + graphology. Add entities, expand their
// links, filter by relation type / status / sensitivity / period, slide through time, find the shortest path
// between two entities, pin and hide, save the view and export it as PNG or SVG. Colors follow the active
// "Color by" mode, shapes the kind, outlines the review status, as in every list. Labels in any script render in
// their own direction (task 24 AC).

export interface GraphSearch {
  /** Entity to start from (e.g. "Open in graph" on a profile). */
  focus?: string;
  /** Saved view to open. */
  view?: string;
}

const str = (v: unknown) => (typeof v === 'string' && /^[0-9A-Za-z]{1,40}$/.test(v) ? v : undefined);

export const Route = createFileRoute('/graph')({
  validateSearch: (s: Record<string, unknown>): GraphSearch => ({ focus: str(s.focus), view: str(s.view) }),
  component: GraphPage,
});

const TOUR = {
  id: 'graph',
  steps: [
    {
      target: '[data-tour="add"]',
      title: 'Start from an entity',
      body: 'Type a name in any script. The entity and everything it links to appear on the graph.',
      learnMore: 'graph-explorer',
    },
    {
      target: '[data-tour="canvas"]',
      title: 'Explore',
      body: 'Click an entity to select it, double-click to expand its links, drag to move it: it pushes the others aside and stays pinned where you drop it. Scroll to zoom.',
    },
    {
      target: '[data-tour="tools"]',
      title: 'Filter and find paths',
      body: 'Narrow the links by type, review status, sensitivity or period, slide through time, or find the shortest path between two entities.',
    },
    {
      target: '[data-tour="views"]',
      title: 'Save and export',
      body: 'Save the graph as a view to reopen later, or export it as PNG or SVG.',
      learnMore: 'graph-explorer',
    },
  ],
};

type Pos = { x: number; y: number };
type PathResult = { ids: string[]; edges: string[] } | { notFound: true; hops: number } | null;

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

const NODE_SIZE = (degree: number) => 12 + Math.min(10, Math.sqrt(degree) * 2.5);

function GraphPage() {
  useScreenTour(TOUR);
  const { data: snap } = useSchema();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: '/graph' });
  const colors = useThemeColors();
  const styleOf = useStyleOf(snap);
  const canExport = useCan('content.export');
  const checklist = useChecklist();
  const confirm = useOptionalConfirm();
  const { withReauth } = useReauth();
  const entitySearch = useEntitySearch(snap);

  // ── State ───────────────────────────────────────────────────────────────
  const [loaded, setLoaded] = useState<Loaded>(emptyLoaded);
  const [anchored, setAnchored] = useState<Set<string>>(() => new Set());
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const [pinned, setPinned] = useState<Set<string>>(() => new Set());
  const [filter, setFilter] = useState<GraphFilter>({});
  const [asOf, setAsOf] = useState<number | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [pathEnds, setPathEnds] = useState<{ from: EntitySuggestion | null; to: EntitySuggestion | null }>({ from: null, to: null });
  const [path, setPath] = useState<PathResult>(null);
  const [avoidHidden, setAvoidHidden] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tools, setTools] = useState<'filters' | 'path' | null>(null);
  const [currentView, setCurrentView] = useState<SavedView | null>(null);
  const [saving, setSaving] = useState(false);
  const [viewName, setViewName] = useState('');
  const [layoutMode, setLayoutMode] = useState<LayoutMode>('force');
  const modeRef = useRef(layoutMode);
  modeRef.current = layoutMode;
  const graphRef = useRef<Graph>(new MultiDirectedGraph<NodeAttrs, EdgeAttrs>());
  const graph = graphRef.current;
  const positions = useRef(new Map<string, Pos>());
  const sigmaRef = useRef<GraphRenderer | null>(null);
  const layoutPending = useRef(false);
  /** A saved view was opened: frame it (and scale positions saved before layouts were in pixels) once it is drawn. */
  const framePending = useRef(false);
  const ids = { save: useId(), from: useId(), to: useId(), add: useId(), layout: useId() };

  // ── Loading ─────────────────────────────────────────────────────────────
  /** Adds a response, keeping the graph under the node cap. */
  const add = useCallback((data: { nodes: GraphNode[]; edges: GraphEdge[] }, truncated = false) => {
    setLoaded(prev => {
      const room = GRAPH_MAX_NODES - prev.nodes.size;
      const fresh = data.nodes.filter(n => !prev.nodes.has(n.id));
      let nodes = data.nodes;
      let edges = data.edges;
      if (fresh.length > room) {
        const keep = new Set([
          ...prev.nodes.keys(),
          ...data.nodes.filter(n => prev.nodes.has(n.id)).map(n => n.id),
          ...fresh.slice(0, Math.max(0, room)).map(n => n.id),
        ]);
        nodes = data.nodes.filter(n => keep.has(n.id));
        edges = data.edges.filter(e => keep.has(e.source) && keep.has(e.target));
        setNotice(`The graph shows at most ${GRAPH_MAX_NODES} entities. Hide some, or narrow the filter, to see more.`);
      } else if (truncated) setNotice('Some links were left out: there were too many at once. Narrow the filter or expand fewer entities.');
      layoutPending.current ||= fresh.length > 0;
      return merge(prev, { nodes, edges });
    });
  }, []);

  const run = useCallback(async (label: string, fn: () => Promise<void>) => {
    setBusy(label);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Something went wrong. Try again.');
    } finally {
      setBusy(null);
    }
  }, []);

  const doExpand = useCallback(
    (targets: string[], f: GraphFilter = filter) =>
      run('Loading links…', async () => {
        const r = await expand(targets, f);
        add(r, r.truncated);
        setExpanded(prev => new Set([...prev, ...targets]));
        setAnchored(prev => new Set([...prev, ...targets]));
        setHidden(prev => {
          const next = new Set(prev);
          for (const t of targets) next.delete(t);
          return next;
        });
        checklist.complete('explore-graph');
      }),
    [filter, run, add, checklist],
  );

  // Refetch with the new filter so expansions follow it (links of other types become loadable, the cap applies).
  const filterKey = JSON.stringify(filter);
  const lastFilter = useRef(filterKey);
  // biome-ignore lint/correctness/useExhaustiveDependencies: refetch only when the filter itself changes
  useEffect(() => {
    if (lastFilter.current === filterKey) return;
    lastFilter.current = filterKey;
    if (!expanded.size) return;
    const t = setTimeout(() => {
      run('Applying filters…', async () => {
        const r = await expand([...expanded], filter);
        add(r, r.truncated);
      });
    }, 300);
    return () => clearTimeout(t);
  }, [filterKey]);

  // Open ?focus= and ?view= from the URL.
  const opened = useRef<string | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: once per URL target
  useEffect(() => {
    const key = `${search.focus ?? ''}|${search.view ?? ''}`;
    if (!snap || opened.current === key) return;
    opened.current = key;
    if (search.view) openView(search.view);
    else if (search.focus) {
      // One id, or several comma-separated (a report's results, task 31).
      const ids = search.focus.split(',').filter(Boolean).slice(0, GRAPH_MAX_SEEDS);
      setSelected(ids[0] ?? null);
      doExpand(ids);
    }
  }, [snap, search.focus, search.view]);

  // ── Visible subgraph → graphology ───────────────────────────────────────
  const pathNodes = useMemo(() => (path && 'ids' in path ? path.ids : []), [path]);
  const anchoredAll = useMemo(() => new Set([...anchored, ...pinned, ...pathNodes]), [anchored, pinned, pathNodes]);
  // Radial layouts centre on the selection, else the path, else the entities added or expanded.
  const centersRef = useRef<string[]>([]);
  centersRef.current = selected ? [selected] : pathNodes.length ? pathNodes : [...anchored];
  const visible = useMemo(
    () => visibleSubgraph(loaded, { filter, asOf, hidden, anchored: anchoredAll }),
    [loaded, filter, asOf, hidden, anchoredAll],
  );
  const shown = useMemo(() => {
    const m = new Map<string, number>();
    for (const e of visible.edges) {
      m.set(e.source, (m.get(e.source) ?? 0) + 1);
      m.set(e.target, (m.get(e.target) ?? 0) + 1);
    }
    return m;
  }, [visible]);

  useEffect(() => {
    if (!snap) return;
    const want = new Set(visible.nodes.map(n => n.id));
    graph.forEachNode((id, a) => {
      if (!want.has(id)) {
        positions.current.set(id, { x: a.x, y: a.y });
        graph.dropNode(id);
      }
    });
    const fresh: string[] = [];
    for (const n of visible.nodes) {
      const style = styleOf(n);
      const look = {
        kind: shapeOf(snap, n.typeId, n.kind),
        status: n.reviewStatus,
        fill: colors.entity(style.colorSlot),
        stroke: colors.foreground,
        fade: style.fade,
        ring: style.ring ? (style.ringSlot ? colors.entity(style.ringSlot) : colors.foreground) : null,
        confidential: n.sensitivity === 'confidential',
        warning: colors.warning,
        badge: colors.surface,
      };
      const attrs = {
        size: NODE_SIZE(n.degree),
        label: shortLabel(n.displayName),
        fullLabel: n.displayName,
        image: nodeImage(look),
        dimImage: nodeImage({ ...look, fade: true }),
        color: TRANSPARENT,
        type: 'image' as const,
        fixed: pinned.has(n.id),
      };
      if (graph.hasNode(n.id)) graph.mergeNodeAttributes(n.id, attrs);
      else {
        const p = positions.current.get(n.id);
        graph.addNode(n.id, { ...attrs, x: p?.x ?? 0, y: p?.y ?? 0 });
        if (!p) fresh.push(n.id);
      }
    }
    const wantEdges = new Set(visible.edges.map(e => e.id));
    graph.forEachEdge(id => {
      if (!wantEdges.has(id)) graph.dropEdge(id);
    });
    for (const e of visible.edges) {
      const attrs: EdgeAttrs = {
        size: edgeWidth(e),
        color:
          e.status === 'disputed'
            ? colors.warning
            : e.status === 'unreviewed'
              ? withAlpha(colors.muted, 0.55)
              : withAlpha(colors.muted, 0.85),
        label: [e.label, e.periodText, e.sensitivity === 'confidential' ? 'confidential' : null].filter(Boolean).join(' · '),
        type: e.directed ? 'arrow' : 'line',
      };
      if (graph.hasEdge(e.id)) graph.mergeEdgeAttributes(e.id, attrs);
      else graph.addDirectedEdgeWithKey(e.id, e.source, e.target, attrs);
    }
    if (fresh.length || layoutPending.current) {
      layoutPending.current = false;
      framePending.current = false;
      // Many new entities at once (a first load, a big expansion): lay everything out again, pins stay. A few:
      // they go round what they link to and the rest stays where it is.
      const old = graph.order - fresh.length;
      const full = old === 0 || fresh.length >= Math.max(8, old / 2);
      arrange(graph, sigmaRef.current, modeRef.current, {
        keep: pinned,
        fresh: full ? undefined : new Set(fresh),
        centers: centersRef.current,
      });
    } else if (framePending.current && graph.order) {
      framePending.current = false;
      frameSaved(graph, sigmaRef.current);
    }
  }, [visible, snap, styleOf, colors, pinned, graph]);

  // ── Actions ─────────────────────────────────────────────────────────────
  const addEntity = (s: EntitySuggestion) => {
    setSelected(s.id);
    doExpand([s.id]);
  };
  const hide = (id: string) => {
    setHidden(prev => new Set(prev).add(id));
    setAnchored(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    if (selected === id) setSelected(null);
  };
  const togglePin = (id: string) =>
    setPinned(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const moved = useCallback(
    (id: string) => {
      const a = graph.getNodeAttributes(id);
      positions.current.set(id, { x: a.x, y: a.y });
      setPinned(prev => (prev.has(id) ? prev : new Set(prev).add(id)));
    },
    [graph],
  );
  /** Keyboard move (the selection panel's arrows): a step on screen, pushing aside as a drag does, then pinned. */
  const nudge = (id: string, dx: number, dy: number) => {
    const r = sigmaRef.current;
    if (!r || !graph.hasNode(id)) return;
    const a = graph.getNodeAttributes(id);
    const v = r.graphToViewport({ x: a.x, y: a.y });
    const g = r.viewportToGraph({ x: v.x + dx, y: v.y + dy });
    graph.mergeNodeAttributes(id, { x: g.x, y: g.y });
    pushFrom(r, id, pinned);
    moved(id);
  };
  const unpinAll = () => {
    setPinned(new Set());
    setNotice('All entities unpinned. Re-arrange lays them out again.');
  };

  const findPath = () => {
    const { from, to } = pathEnds;
    if (!from || !to) return;
    run('Finding a path…', async () => {
      const f = asOf ? { ...filter, from: String(asOf), to: String(asOf) } : filter;
      const r = await shortestPath(from.id, to.id, f, avoidHidden ? [...hidden] : []);
      if (!r.found) {
        setPath({ notFound: true, hops: r.searched });
        return;
      }
      add(r);
      setAnchored(prev => new Set([...prev, ...r.path]));
      setPath({ ids: r.path, edges: r.edges.map(e => e.id) });
      checklist.complete('explore-graph');
    });
  };
  const clearPath = () => setPath(null);
  const pathHighlight = useMemo(() => (path && 'ids' in path ? { nodes: new Set(path.ids), edges: new Set(path.edges) } : null), [path]);

  /** Re-arrange: the chosen layout from scratch over the whole canvas; pins are released (drag to pin again). */
  const rearrange = (mode: LayoutMode = layoutMode) => {
    const hadPins = pinned.size > 0;
    graph.forEachNode(id => graph.setNodeAttribute(id, 'fixed', false));
    setPinned(new Set());
    arrange(graph, sigmaRef.current, mode, { keep: new Set(), centers: centersRef.current });
    graph.forEachNode((id, a) => positions.current.set(id, { x: a.x, y: a.y }));
    const name = LAYOUT_MODES.find(m => m.mode === mode)?.label ?? '';
    setNotice(`Re-arranged ${plural(graph.order, 'entity', 'entities')}: ${name.toLowerCase()}${hadPins ? '. Pins were released' : ''}.`);
  };

  const viewState = (): GraphViewState => ({
    version: 1,
    nodes: visible.nodes.map(n => {
      const a = graph.hasNode(n.id) ? graph.getNodeAttributes(n.id) : null;
      return { id: n.id, x: a?.x ?? 0, y: a?.y ?? 0, ...(pinned.has(n.id) ? { pinned: true } : {}) };
    }),
    hidden: [...hidden],
    filter,
    asOf,
    path: pathEnds.from && pathEnds.to && path && 'ids' in path ? { from: pathEnds.from.id, to: pathEnds.to.id } : null,
    expanded: [...expanded].filter(id => loaded.nodes.has(id)),
  });

  const { save, remove } = useViewMutations();
  const views = useSavedViews();

  async function openView(id: string) {
    await run('Opening the view…', async () => {
      const v = await api<SavedView>(`/views/${id}`);
      const s = v.state;
      positions.current = new Map(s.nodes.map(n => [n.id, { x: n.x, y: n.y }]));
      graph.clear();
      framePending.current = true;
      const nodeIds = s.nodes.map(n => n.id);
      const [ex, among] = await Promise.all([
        s.expanded.length ? expand(s.expanded, s.filter) : Promise.resolve({ nodes: [], edges: [], truncated: false }),
        linksAmong(nodeIds, s.filter),
      ]);
      // Only what the view showed (and hid): expansions may have grown since, and new neighbours are one click away.
      const keep = new Set([...nodeIds, ...s.hidden]);
      const data = {
        nodes: [...ex.nodes, ...among.nodes].filter(n => keep.has(n.id)),
        edges: [...ex.edges, ...among.edges].filter(e => keep.has(e.source) && keep.has(e.target)),
      };
      setLoaded(merge(emptyLoaded(), data));
      setAnchored(new Set(nodeIds));
      setExpanded(new Set(s.expanded));
      setHidden(new Set(s.hidden));
      setPinned(new Set(s.nodes.filter(n => n.pinned).map(n => n.id)));
      lastFilter.current = JSON.stringify(s.filter);
      setFilter(s.filter);
      setAsOf(s.asOf);
      setSelected(null);
      setPath(null);
      setPathEnds({ from: null, to: null });
      setCurrentView(v);
      setViewName(v.name);
      const missing = nodeIds.length - data.nodes.length;
      setNotice(
        missing > 0
          ? `${missing} ${missing === 1 ? 'entity in this view no longer exists' : 'entities in this view no longer exist'} (merged or erased).`
          : null,
      );
      if (s.path) {
        const r = await shortestPath(
          s.path.from,
          s.path.to,
          s.asOf ? { ...s.filter, from: String(s.asOf), to: String(s.asOf) } : s.filter,
          [],
        );
        if (r.found) {
          add(r);
          setPath({ ids: r.path, edges: r.edges.map(e => e.id) });
        }
      }
    });
  }

  const submitSave = async (asNew: boolean) => {
    const name = viewName.trim();
    if (!name) return;
    try {
      const v = await save.mutateAsync({ id: asNew ? undefined : currentView?.id, name, state: viewState() });
      setCurrentView(v);
      setSaving(false);
      setNotice(`Saved “${v.name}”.`);
      navigate({ search: { view: v.id }, replace: true });
      opened.current = `|${v.id}`;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The view could not be saved.');
    }
  };

  const deleteView = async (v: SavedView) => {
    if (
      confirm &&
      !(await confirm({
        title: 'Delete this view?',
        body: `“${v.name}” will be deleted. The entities and links stay.`,
        confirmLabel: 'Delete view',
        destructive: true,
      }))
    )
      return;
    await remove.mutateAsync(v.id);
    if (currentView?.id === v.id) {
      setCurrentView(null);
      navigate({ search: {}, replace: true });
    }
  };

  const doExport = (format: 'png' | 'svg') =>
    run(`Exporting ${format.toUpperCase()}…`, async () => {
      const r = sigmaRef.current;
      if (!r) throw new ApiError(400, 'invalid', 'The graph isn’t drawn yet.');
      // An export: a fresh authenticator code first when the last one is older than 15 minutes (PLAN §11).
      const { watermark } = await withReauth(() =>
        api<{ watermark: string }>('/graph/exports', {
          body: { format, nodes: visible.nodes.length, edges: visible.edges.length, viewId: currentView?.id },
        }),
      );
      if (format === 'png') await exportPng(r, watermark, colors);
      else exportSvg(r, watermark, colors);
    });

  const clearAll = () => {
    graph.clear();
    sigmaRef.current?.setCustomBBox(null);
    positions.current.clear();
    setLoaded(emptyLoaded());
    setAnchored(new Set());
    setExpanded(new Set());
    setHidden(new Set());
    setPinned(new Set());
    setSelected(null);
    setPath(null);
    setCurrentView(null);
    setNotice(null);
    navigate({ search: {}, replace: true });
  };

  // ── Render ──────────────────────────────────────────────────────────────
  const predicates = useMemo(() => {
    const m = new Map<string, { key: string; label: string; count: number }>();
    for (const e of loaded.edges.values()) {
      const p = m.get(e.predicate) ?? { key: e.predicate, label: e.label, count: 0 };
      p.count++;
      m.set(e.predicate, p);
    }
    for (const k of filter.predicates ?? [])
      if (!m.has(k)) m.set(k, { key: k, label: snap?.relationTypes.find(r => r.key === k)?.label ?? k, count: 0 });
    return [...m.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [loaded.edges, filter.predicates, snap]);
  const years = useMemo(() => edgeYears([...loaded.edges.values()]), [loaded.edges]);
  const selectedNode = selected ? loaded.nodes.get(selected) : undefined;
  const hiddenNodes = [...hidden].flatMap(id => (loaded.nodes.has(id) ? [loaded.nodes.get(id) as GraphNode] : []));
  const sortedNodes = useMemo(() => [...visible.nodes].sort((a, b) => a.displayName.localeCompare(b.displayName)), [visible.nodes]);
  const filtersOn = !!(filter.predicates?.length || filter.statuses?.length || filter.sensitivity || filter.from || filter.to || asOf);
  const empty = loaded.nodes.size === 0;

  if (!snap) return <PageHeader title="Graph" />;

  return (
    <>
      <PageHeader
        title="Graph"
        description="Explore how actors, events, places and assets are linked. Colors, shapes and outlines mean the same as in every list."
        actions={
          <div className="flex flex-wrap items-center justify-end gap-2" data-tour="views">
            <ViewsMenu
              views={views.data?.views ?? []}
              current={currentView?.id ?? null}
              onOpen={v => navigate({ search: { view: v.id } })}
              onDelete={deleteView}
            />
            <Button size="sm" variant="outline" disabled={empty} onClick={() => setSaving(s => !s)} aria-expanded={saving}>
              <Save aria-hidden="true" /> Save view
            </Button>
            {canExport && (
              <>
                <Button size="sm" variant="outline" disabled={empty || !!busy} onClick={() => doExport('png')}>
                  <Download aria-hidden="true" /> PNG
                </Button>
                <Button size="sm" variant="outline" disabled={empty || !!busy} onClick={() => doExport('svg')}>
                  <Download aria-hidden="true" /> SVG
                </Button>
              </>
            )}
          </div>
        }
      />
      {saving && (
        <form
          aria-label="Save view"
          className="mb-4 flex flex-wrap items-end gap-2 rounded-lg border bg-surface p-3"
          onSubmit={e => {
            e.preventDefault();
            submitSave(!currentView);
          }}
        >
          <div className="min-w-64 flex-1 space-y-1">
            <label htmlFor={ids.save} className="text-xs font-medium">
              View name
            </label>
            <input
              id={ids.save}
              value={viewName}
              onChange={e => setViewName(e.target.value)}
              placeholder="e.g. Meridian money trail"
              maxLength={120}
              className="h-9 w-full rounded-md border border-input bg-surface px-2 text-sm"
              dir="auto"
              // biome-ignore lint/a11y/noAutofocus: the form opens on request; typing the name is the next step
              autoFocus
            />
          </div>
          <p className="basis-full text-xs text-muted-foreground order-last">
            Saves the entities on screen and where they are, pins, hidden entities, filters, the time slider and the path. Only you see your
            views.
          </p>
          {currentView ? (
            <>
              <Button type="submit" size="sm" disabled={!viewName.trim() || save.isPending}>
                Update “{currentView.name}”
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={!viewName.trim() || save.isPending}
                onClick={() => submitSave(true)}
              >
                Save as new view
              </Button>
            </>
          ) : (
            <Button type="submit" size="sm" disabled={!viewName.trim() || save.isPending}>
              Save
            </Button>
          )}
          <Button type="button" size="sm" variant="ghost" onClick={() => setSaving(false)}>
            Cancel
          </Button>
        </form>
      )}
      <Tip id="graph" learnMore="graph-explorer">
        Double-click an entity to expand its links. Drag one to move it: it pushes the others aside and stays pinned where you drop it.
        Crowded? Re-arrange. The list beside the graph does the same from the keyboard.
      </Tip>

      <div className="mt-4 flex flex-wrap items-end gap-2">
        <div className="min-w-72 flex-1 space-y-1" data-tour="add">
          <div className="flex items-center gap-1">
            <label htmlFor={ids.add} className="text-xs font-medium">
              Add to the graph
            </label>
            <FieldHelp
              label="Add to the graph"
              help="Any name, alias or ID code, in any script. The entity is added with everything it links to."
              example="Hassan Haddad, حسن حداد or POL_HADDAD"
              learnMore="graph-explorer"
            />
          </div>
          <EntityAutocomplete
            id={ids.add}
            placeholder="Type a name, alias or ID code"
            search={q => entitySearch(null, q)}
            onSelect={addEntity}
          />
        </div>
        <div className="flex flex-wrap gap-2" data-tour="tools">
          <Button
            size="md"
            variant={tools === 'filters' ? 'secondary' : 'outline'}
            aria-expanded={tools === 'filters'}
            onClick={() => setTools(t => (t === 'filters' ? null : 'filters'))}
          >
            <Filter aria-hidden="true" /> Filters
            {filtersOn && <span className="rounded-full bg-foreground px-1.5 text-xs text-background">on</span>}
          </Button>
          <Button
            size="md"
            variant={tools === 'path' ? 'secondary' : 'outline'}
            aria-expanded={tools === 'path'}
            onClick={() => setTools(t => (t === 'path' ? null : 'path'))}
          >
            <RouteIcon aria-hidden="true" /> Find a path
          </Button>
          <div className="flex items-center gap-1">
            <Button size="md" variant="outline" onClick={() => rearrange()} disabled={empty}>
              <Shuffle aria-hidden="true" /> Re-arrange
            </Button>
            <label htmlFor={ids.layout} className="sr-only">
              Layout
            </label>
            <select
              id={ids.layout}
              value={layoutMode}
              disabled={empty}
              title={LAYOUT_MODES.find(m => m.mode === layoutMode)?.help}
              onChange={e => {
                const mode = e.target.value as LayoutMode;
                setLayoutMode(mode);
                rearrange(mode);
              }}
              className="h-9 rounded-md border border-input bg-surface px-2 text-sm disabled:opacity-50"
            >
              {LAYOUT_MODES.map(m => (
                <option key={m.mode} value={m.mode}>
                  {m.label}
                </option>
              ))}
            </select>
            <FieldHelp
              label="Re-arrange"
              help="Lays every entity out again from scratch over the whole canvas, with room for each name, and releases pins. Spread out: groups apart. Around the selection: rings around the selected entity. Follow the arrows: columns in the direction of the links."
              learnMore="graph-explorer"
            />
          </div>
          {pinned.size > 0 && (
            <Button size="md" variant="ghost" onClick={unpinAll}>
              <PinOff aria-hidden="true" /> Unpin all
            </Button>
          )}
          <Button size="md" variant="ghost" onClick={clearAll} disabled={empty}>
            <X aria-hidden="true" /> Clear
          </Button>
        </div>
      </div>

      {tools === 'filters' && (
        <div className="mt-3 rounded-lg border bg-surface p-3">
          <FilterPanel filter={filter} onChange={setFilter} predicates={predicates} years={years} asOf={asOf} onAsOf={setAsOf} />
        </div>
      )}
      {tools === 'path' && (
        <form
          aria-label="Find a path"
          className="mt-3 grid gap-3 rounded-lg border bg-surface p-3 md:grid-cols-[1fr_1fr_auto]"
          onSubmit={e => {
            e.preventDefault();
            findPath();
          }}
        >
          <div className="space-y-1">
            <label htmlFor={ids.from} className="text-xs font-medium">
              From
            </label>
            <EntityAutocomplete
              key={`from-${pathEnds.from?.id ?? ''}`}
              id={ids.from}
              defaultQuery={pathEnds.from?.name ?? ''}
              placeholder="Type a name"
              search={q => entitySearch(null, q)}
              onSelect={s => setPathEnds(p => ({ ...p, from: s }))}
            />
          </div>
          <div className="space-y-1">
            <label htmlFor={ids.to} className="text-xs font-medium">
              To
            </label>
            <EntityAutocomplete
              key={`to-${pathEnds.to?.id ?? ''}`}
              id={ids.to}
              defaultQuery={pathEnds.to?.name ?? ''}
              placeholder="Type a name"
              search={q => entitySearch(null, q)}
              onSelect={s => setPathEnds(p => ({ ...p, to: s }))}
            />
          </div>
          <div className="flex items-end gap-2">
            <Button type="submit" disabled={!pathEnds.from || !pathEnds.to || !!busy}>
              Find path
            </Button>
            {path && (
              <Button type="button" variant="ghost" onClick={clearPath}>
                Clear path
              </Button>
            )}
          </div>
          <label className="flex items-center gap-2 text-sm md:col-span-3">
            <input type="checkbox" checked={avoidHidden} onChange={e => setAvoidHidden(e.target.checked)} />
            Don’t go through hidden entities
          </label>
          <p className="text-xs text-muted-foreground md:col-span-3">
            The shortest chain of links (up to 6) between the two, following links either way and using the current filters.
          </p>
        </form>
      )}

      <div aria-live="polite" className="mt-3 min-h-5 text-sm">
        {busy ? (
          <span className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="size-4 animate-spin" aria-hidden="true" /> {busy}
          </span>
        ) : error ? (
          <span role="alert" className="text-danger-foreground">
            {error}
          </span>
        ) : (
          notice && <span className="text-muted-foreground">{notice}</span>
        )}
      </div>

      {path && (
        <Section
          title={'ids' in path ? `Path: ${path.ids.length - 1} ${path.ids.length === 2 ? 'link' : 'links'}` : 'No path found'}
          actions={
            <Button size="sm" variant="ghost" onClick={clearPath}>
              Clear path
            </Button>
          }
        >
          {'ids' in path ? (
            <ol aria-label="Path" className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm" data-testid="graph-path">
              {path.ids.map((id, i) => {
                const n = loaded.nodes.get(id);
                const e =
                  i > 0
                    ? loaded.edges.get(
                        path.edges.find(x => {
                          const edge = loaded.edges.get(x);
                          const prev = path.ids[i - 1];
                          return edge && ((edge.source === prev && edge.target === id) || (edge.target === prev && edge.source === id));
                        }) ?? '',
                      )
                    : undefined;
                const prev = path.ids[i - 1];
                return (
                  <li key={id} className="flex items-center gap-2">
                    {e && <span className="text-muted-foreground">→ {e.source === prev ? e.label : (e.inverseLabel ?? e.label)} →</span>}
                    {n ? (
                      <button type="button" className="rounded hover:underline" onClick={() => setSelected(id)}>
                        <NodeName snap={snap} node={n} />
                      </button>
                    ) : (
                      id
                    )}
                  </li>
                );
              })}
            </ol>
          ) : (
            <p className="text-sm text-muted-foreground">
              These two aren’t connected within {path.hops} {path.hops === 1 ? 'link' : 'links'} under the current filters
              {avoidHidden && hidden.size ? ' without going through hidden entities' : ''}. Try clearing filters or showing hidden entities.
            </p>
          )}
        </Section>
      )}

      <div className="mt-3 grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="relative h-[65vh] min-h-96 overflow-hidden rounded-lg border bg-background" data-tour="canvas">
          {empty && !busy ? (
            <div className="absolute inset-0 z-10 flex items-center justify-center p-6">
              <EmptyState icon={Network} title="Start from an entity" learnMore="graph-explorer">
                Type a name above, or open a saved view. You can also open the graph from any profile’s Connections panel.
              </EmptyState>
            </div>
          ) : null}
          <GraphCanvas
            graph={graph}
            colors={colors}
            selected={selected}
            path={pathHighlight}
            onSelect={setSelected}
            onExpand={id => doExpand([id])}
            onMoved={moved}
            pinned={pinned}
            onReady={s => {
              sigmaRef.current = s;
            }}
            label={`Network graph: ${plural(visible.nodes.length, 'entity', 'entities')}, ${plural(visible.edges.length, 'link')}. The list beside it has the same entities and actions.`}
          />
          <p className="pointer-events-none absolute start-2 bottom-2 rounded bg-surface/80 px-2 py-0.5 text-xs text-muted-foreground">
            <DataNumber>{visible.nodes.length}</DataNumber> entities · <DataNumber>{visible.edges.length}</DataNumber> links
            {asOf && (
              <>
                {' '}
                · in <DataNumber>{asOf}</DataNumber>
              </>
            )}
          </p>
        </div>
        <div className="space-y-3">
          {selectedNode && (
            <SelectionPanel
              snap={snap}
              node={selectedNode}
              shownLinks={shown.get(selectedNode.id) ?? 0}
              links={visible.edges.filter(e => e.source === selectedNode.id || e.target === selectedNode.id)}
              nodes={loaded.nodes}
              pinned={pinned.has(selectedNode.id)}
              expanded={expanded.has(selectedNode.id)}
              onExpand={() => doExpand([selectedNode.id])}
              onPin={() => togglePin(selectedNode.id)}
              onMove={(dx, dy) => nudge(selectedNode.id, dx, dy)}
              onHide={() => hide(selectedNode.id)}
              onPathFrom={() => {
                setPathEnds(p => ({ ...p, from: suggestion(snap, selectedNode) }));
                setTools('path');
              }}
              onPathTo={() => {
                setPathEnds(p => ({ ...p, to: suggestion(snap, selectedNode) }));
                setTools('path');
              }}
              onClose={() => setSelected(null)}
            />
          )}
          {visible.nodes.length > 0 && (
            <Section title="Legend">
              <ColorLegend styles={visible.nodes.map(n => styleOf(n))} />
              <p className="mt-2 text-xs text-muted-foreground">
                Shape = kind · solid outline = verified, dashed = unreviewed, double with ⚠ = disputed ·{' '}
                <Lock className="inline size-3" aria-label="lock" /> = confidential. Thicker links carry larger amounts; amber links are
                disputed.
              </p>
            </Section>
          )}
          {sortedNodes.length > 0 && (
            <Section title={`On the graph (${sortedNodes.length})`}>
              <EntityList
                snap={snap}
                nodes={sortedNodes}
                shown={shown}
                selected={selected}
                pinned={pinned}
                onSelect={setSelected}
                onExpand={id => doExpand([id])}
                onHide={hide}
              />
            </Section>
          )}
          {hiddenNodes.length > 0 && (
            <HiddenList
              snap={snap}
              nodes={hiddenNodes}
              onShow={id =>
                setHidden(prev => {
                  const next = new Set(prev);
                  next.delete(id);
                  return next;
                })
              }
              onShowAll={() => setHidden(new Set())}
            />
          )}
        </div>
      </div>
    </>
  );
}

function suggestion(snap: SchemaSnapshot, n: GraphNode): EntitySuggestion {
  return {
    id: n.id,
    name: n.displayName,
    kind: shapeOf(snap, n.typeId, n.kind),
    typeLabel: '',
    status: n.reviewStatus,
    confidential: n.sensitivity === 'confidential',
    why: n.style ? whyText(n.style) : undefined,
    detail: `${n.code} · ${REVIEW_STATUS_LABELS[n.reviewStatus]}`,
  };
}

/**
 * Lays the graph out (layout.ts) in pixels over the canvas, writes the positions and frames every mark and name.
 * With `fresh`, only those entities are placed; `keep` (pinned) never move.
 */
function arrange(
  graph: Graph,
  renderer: GraphRenderer | null,
  mode: LayoutMode,
  opts: { keep: ReadonlySet<string>; fresh?: ReadonlySet<string>; centers: string[] },
) {
  if (!graph.order) return;
  const { width, height } = layoutArea(renderer);
  const r = computeLayout({
    nodes: graph.mapNodes((id, a) => layoutNodeOf(id, a)),
    edges: graph.mapEdges((_edge, _attrs, source, target) => ({ source, target })),
    width,
    height,
    mode,
    centers: opts.centers,
    keep: opts.keep,
    fresh: opts.fresh,
  });
  for (const [id, p] of r.positions) graph.mergeNodeAttributes(id, p);
  if (renderer) fitToGraph(renderer, 300);
}

/**
 * Frames an opened saved view. Views saved before layouts were in pixels hold positions a few units apart: those
 * are scaled up to the canvas (the picture stays the same) so names get the room they take on screen.
 */
function frameSaved(graph: Graph, renderer: GraphRenderer | null) {
  if (!renderer) return;
  const { width, height } = layoutArea(renderer);
  const xs = graph.mapNodes((_id, a) => a.x);
  const ys = graph.mapNodes((_id, a) => a.y);
  const w = Math.max(...xs) - Math.min(...xs);
  const h = Math.max(...ys) - Math.min(...ys);
  if (graph.order > 1 && Math.max(w, h) < Math.min(width, height) / 2) {
    const k = Math.min(w ? width / w : Number.POSITIVE_INFINITY, h ? height / h : Number.POSITIVE_INFINITY) * 0.9;
    if (Number.isFinite(k)) graph.forEachNode((id, a) => graph.mergeNodeAttributes(id, { x: a.x * k, y: a.y * k }));
  }
  fitToGraph(renderer, 0);
}
